/**
 * @copyright (C) 2024, rsupport. All rights reserved
 *
 * @license
 * This software and/or source code may be used, copied and/or disseminated only
 * with the written permission of rsupport, or in accordance with the terms
 * and conditions stipulated in the agreement/contract under which the software
 * and/or source code has been supplied by rsupport or its affiliates.
 * Unauthorized use, copying, or dissemination of this file, via any medium, is
 * strictly prohibited, and will constitute an infringement of copyright.
 *
 * @author
 * Jinhyoung Jang (jangjh@rsupport.com)
 *
 * @description
 *
 * @created at 2024-08-23 15:52:49
 **/

import { BaseServiceV2 } from "@/frameworks/libraries/restservice";

class PartnerAdminDefaultProfileImage extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/default_profile_images${id ? `/${id}` : ""}`;

	public remove = {
		name: "",
		execute: ({
			id
		}: WebServices.PartnerAdmin.DefaultProfileImage.Remove.Request): Promise<WebServices.PartnerAdmin.DefaultProfileImage.Remove.Response> =>
			this.delete(this.remove.name, this.APIPath(id))
	};

	public detail = {
		name: "",
		execute: ({
			id
		}: WebServices.PartnerAdmin.DefaultProfileImage.Detail.Request): Promise<WebServices.PartnerAdmin.DefaultProfileImage.Detail.Response> =>
			this.get(this.detail.name, this.APIPath(id))
	};

	public search = {
		name: "",
		execute: (
			parameter: WebServices.PartnerAdmin.DefaultProfileImage.Search.Request = {}
		): Promise<WebServices.PartnerAdmin.DefaultProfileImage.Search.Response> =>
			this.get(this.search.name, this.APIPath(), parameter)
	};

	public add = {
		name: "",
		execute: (
			parameter: WebServices.PartnerAdmin.DefaultProfileImage.Add.Request
		): Promise<WebServices.PartnerAdmin.DefaultProfileImage.Add.Response> =>
			this.post(this.add.name, this.APIPath(), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};

	public update = {
		name: "",
		execute: ({
			id,
			parameter
		}: {
			id: string;
			parameter: WebServices.PartnerAdmin.DefaultProfileImage.Update.Request;
		}): Promise<WebServices.PartnerAdmin.DefaultProfileImage.Update.Response> =>
			this.put(this.update.name, this.APIPath(id), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};
}

class PartnerAdminDefaultUserStats extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/default_user_status${id ? `/${id}` : ""}`;

	public remove = {
		name: "partneradmin:defaultuserstatus:remove",
		execute: ({
			id
		}: WebServices.PartnerAdmin.DefaultUserStatus.Remove.Request): Promise<WebServices.PartnerAdmin.DefaultUserStatus.Remove.Response> =>
			this.delete(this.remove.name, this.APIPath(id))
	};

	public detail = {
		name: "partneradmin:defaultuserstatus:detail",
		execute: ({
			id
		}: WebServices.PartnerAdmin.DefaultUserStatus.Detail.Request): Promise<WebServices.PartnerAdmin.DefaultUserStatus.Detail.Response> =>
			this.get(this.detail.name, this.APIPath(id))
	};

	public search = {
		name: "partneradmin:defaultuserstatus:search",
		execute: (
			parameter: WebServices.PartnerAdmin.DefaultUserStatus.Search.Request = {}
		): Promise<WebServices.PartnerAdmin.DefaultUserStatus.Search.Response> =>
			this.get(this.search.name, this.APIPath(), parameter)
	};

	public add = {
		name: "partneradmin:defaultuserstatus:add",
		execute: (
			formData: WebServices.PartnerAdmin.DefaultUserStatus.Add.Request
		): Promise<WebServices.PartnerAdmin.DefaultUserStatus.Add.Response> =>
			this.post(this.add.name, this.APIPath(), formData, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};

	public update = {
		name: "partneradmin:defaultuserstatus:update",
		execute: ({
			id,
			formData
		}: {
			id: string;
			formData: WebServices.PartnerAdmin.DefaultUserStatus.Update.Request;
		}): Promise<WebServices.PartnerAdmin.DefaultUserStatus.Update.Response> =>
			this.put(this.update.name, this.APIPath(id), formData, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};
}

class PartnerAdminEmoticon extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/emoticons${id ? `/${id}` : ""}`;

	public remove = {
		name: "partneradmin:emoticon:remove",
		execute: ({
			id
		}: WebServices.PartnerAdmin.Emoticon.Remove.Request): Promise<WebServices.PartnerAdmin.Emoticon.Remove.Response> =>
			this.delete(this.remove.name, this.APIPath(id))
	};

	public detail = {
		name: "partneradmin:emoticon:detail",
		execute: ({
			id
		}: WebServices.PartnerAdmin.Emoticon.Detail.Request): Promise<WebServices.PartnerAdmin.Emoticon.Detail.Response> =>
			this.get(this.detail.name, this.APIPath(id))
	};

	public list = {
		name: "partneradmin:emoticon:list",
		execute: (
			parameter: WebServices.PartnerAdmin.Emoticon.List.Request = {}
		): Promise<WebServices.PartnerAdmin.Emoticon.List.Response> =>
			this.get(this.list.name, this.APIPath(), parameter)
	};

	public add = {
		name: "partneradmin:emoticon:add",
		execute: (formData: WebServices.PartnerAdmin.Emoticon.Add.Request) =>
			this.post(this.add.name, this.APIPath(), formData, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};

	public update = {
		name: "partneradmin:emoticon:update",
		execute: ({
			id,
			formData
		}: {
			id: string;
			formData: WebServices.PartnerAdmin.Emoticon.Update.Request;
		}): Promise<WebServices.PartnerAdmin.Emoticon.Update.Response> =>
			this.post(this.update.name, this.APIPath(id), formData, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};
}

class PartnerAdminSpaceMap extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/space_maps${id ? `/${id}` : ""}`;

	public search = {
		name: "partneradmin:spacemap:search",
		execute: (
			parameter: WebServices.PartnerAdmin.SpaceMap.Search.Request = {}
		): Promise<WebServices.PartnerAdmin.SpaceMap.Search.Response> =>
			this.get(this.search.name, this.APIPath(), parameter)
	};

	public save = {
		name: "partneradmin:spacemap:save",
		execute: (
			parameter: WebServices.PartnerAdmin.SpaceMap.Save.Request
		): Promise<WebServices.PartnerAdmin.SpaceMap.Save.Response> =>
			this.post(this.save.name, this.APIPath(), parameter)
	};

	public detail = {
		name: "partneradmin:spacemap:detail",
		execute: ({
			id,
			...parameter
		}: WebServices.PartnerAdmin.SpaceMap.Detail.Request): Promise<WebServices.PartnerAdmin.SpaceMap.Detail.Response> =>
			this.get(this.detail.name, this.APIPath(id), parameter)
	};

	public update = {
		name: "partneradmin:spacemap:update",
		execute: ({
			id,
			...parameter
		}: WebServices.PartnerAdmin.SpaceMap.Update.Request): Promise<WebServices.PartnerAdmin.SpaceMap.Update.Response> =>
			this.put(this.update.name, this.APIPath(id), parameter)
	};

	public label = {
		map: {
			name: "partneradmin:spacemap:label::map",
			execute: (
				parameter: WebServices.PartnerAdmin.SpaceMap.Label.Map.Request = {}
			): Promise<WebServices.PartnerAdmin.SpaceMap.Label.Map.Response> =>
				this.get(this.label.map.name, this.APIPath("labelValues"), parameter)
		},
		scene: {
			name: "partneradmin:spacemap:label::scene",
			execute: ({
				id,
				...parameter
			}: WebServices.PartnerAdmin.SpaceMap.Label.Scene.Request): Promise<WebServices.PartnerAdmin.SpaceMap.Label.Scene.Response> =>
				this.get(this.label.scene.name, this.APIPath(`labelValues/${id}`), parameter)
		}
	};
}

class PartnerAdminSpaceTheme extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/space_themes${id ? `/${id}` : ""}`;

	public list = {
		name: "partneradmin:spacetheme:search",
		execute: (
			parameter: WebServices.PartnerAdmin.SpaceTheme.List.Request = {}
		): Promise<WebServices.PartnerAdmin.SpaceTheme.List.Response> =>
			this.get(this.list.name, this.APIPath(), parameter)
	};

	public save = {
		name: "partneradmin:spacetheme:save",
		execute: (
			formData: WebServices.PartnerAdmin.SpaceTheme.Save.Request
		): Promise<WebServices.PartnerAdmin.SpaceTheme.Save.Response> =>
			this.post(this.save.name, this.APIPath(), formData, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};

	public detail = {
		name: "partneradmin:spacetheme:detail",
		execute: ({
			id,
			...parameter
		}: WebServices.PartnerAdmin.SpaceTheme.Detail.Request): Promise<WebServices.PartnerAdmin.SpaceTheme.Detail.Response> =>
			this.get(this.detail.name, this.APIPath(id), parameter)
	};

	public update = {
		name: "partneradmin:spacetheme:update",
		execute: ({
			id,
			formData
		}: {
			id: string;
			formData: WebServices.PartnerAdmin.SpaceTheme.Update.Request;
		}): Promise<WebServices.PartnerAdmin.SpaceTheme.Update.Response> =>
			this.post(this.update.name, this.APIPath(id), formData, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};

	public remove = {
		name: "partneradmin:spacetheme:delete",
		execute: ({
			id,
			...parameter
		}: WebServices.PartnerAdmin.SpaceTheme.Delete.Request): Promise<WebServices.PartnerAdmin.SpaceTheme.Delete.Response> =>
			this.delete(this.remove.name, this.APIPath(id), parameter)
	};
}

class Partner extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partners${id ? `/${id}` : ""}`;

	public list = {
		name: "partner:list",
		execute: (parameter: WebServices.Partner.List.Request = {}): Promise<WebServices.Partner.List.Response> =>
			this.get(this.list.name, this.APIPath(), parameter)
	};

	public detail = {
		name: "partner:detail",
		execute: ({ id }: WebServices.Partner.Detail.Request): Promise<WebServices.Partner.Detail.Response> =>
			this.get(this.detail.name, this.APIPath(id))
	};

	public add = {
		name: "partner:add",
		execute: (parameter: WebServices.Partner.Add.Request): Promise<WebServices.Partner.Add.Response> =>
			this.post(this.add.name, this.APIPath(), parameter)
	};
}

class PartnerUser extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_users${id ? `/${id}` : ""}`;

	public list = {
		name: "partnerUser:list",
		execute: (
			parameter: WebServices.PartnerUser.List.Request = {}
		): Promise<WebServices.PartnerUser.List.Response> => this.get(this.list.name, this.APIPath(), parameter)
	};

	public detail = {
		name: "partnerUser:detail",
		execute: ({ id }: WebServices.PartnerUser.Detail.Request): Promise<WebServices.PartnerUser.Detail.Response> =>
			this.get(this.detail.name, this.APIPath(id))
	};

	public add = {
		name: "partnerUser:add",
		execute: (parameter: WebServices.PartnerUser.Add.Request): Promise<WebServices.PartnerUser.Add.Response> =>
			this.post(this.add.name, this.APIPath(), parameter)
	};
}

class PartnerNormalUser extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/users${id ? `/${id}` : ""}`;
	private GroupAPIPath = (path?: string) => `/api/partner_admin/groups${path ? `/${path}` : ""}`;

	public list = {
		name: "partnerUser:list",
		execute: (
			parameter: WebServices.PartnerAdmin.User.List.Request = {}
		): Promise<WebServices.PartnerAdmin.User.List.Response> => this.get(this.list.name, this.APIPath(), parameter)
	};

	public detail = {
		name: "partnerUser:detail",
		execute: ({
			userId
		}: WebServices.PartnerAdmin.User.Detail.Request): Promise<WebServices.PartnerAdmin.User.Detail.Response> =>
			this.get(this.detail.name, this.APIPath(userId))
	};

	public deleteUser = {
		name: "partnerUser:deleteUser",
		execute: ({
			userId
		}: WebServices.PartnerAdmin.User.Delete.Request): Promise<WebServices.PartnerAdmin.User.Delete.Response> =>
			this.delete(this.deleteUser.name, this.APIPath(userId))
	};

	public add = {
		name: "partnerUser:add",
		execute: (
			parameter: WebServices.PartnerAdmin.User.Save.Request
		): Promise<WebServices.PartnerAdmin.User.Save.Response> => this.post(this.add.name, this.APIPath(), parameter)
	};

	public reset_password = {
		name: "partnerUser:reset_password",
		execute: (
			parameter: WebServices.PartnerAdmin.User.ResetPassword.Request
		): Promise<WebServices.PartnerAdmin.User.ResetPassword.Response> =>
			this.post(this.reset_password.name, this.APIPath("password/reset"), parameter)
	};

	public send_auth_mail = {
		name: "partnerUser:send_auth_mail",
		execute: (
			parameter: WebServices.PartnerAdmin.User.SendMail.Request
		): Promise<WebServices.PartnerAdmin.User.SendMail.Response> =>
			this.post(this.send_auth_mail.name, this.APIPath("authentication_mail"), parameter)
	};

	public update_lock = {
		name: "partnerUser:update_lock",
		execute: (
			parameter: WebServices.PartnerAdmin.User.UpdateLock.Request
		): Promise<WebServices.PartnerAdmin.User.UpdateLock.Response> =>
			this.put(this.update_lock.name, this.APIPath("lock"), parameter)
	};

	public update = {
		name: "partnerUser:update",
		execute: ({
			userId,
			...rest
		}: WebServices.PartnerAdmin.User.Update.Request): Promise<WebServices.PartnerAdmin.User.Update.Response> =>
			this.put(this.detail.name, this.APIPath(userId), rest)
	};

	public download_sample = {
		name: "partnerUser:download_sample",
		execute: (
			parameter: WebServices.PartnerAdmin.User.Sample.Request
		): Promise<WebServices.PartnerAdmin.User.Sample.Response> =>
			this.get(this.download_sample.name, this.APIPath("excel_import"), parameter, { responseType: "blob" })
	};

	public bulk_registration = {
		name: "partnerUser:bulk_registration",
		execute: (
			parameter: WebServices.PartnerAdmin.User.Sample.Request
		): Promise<WebServices.PartnerAdmin.User.Sample.Response> =>
			this.post(this.bulk_registration.name, this.APIPath("excel_import"), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};

	public group = {
		list: {
			name: "partnerUser:group::list",
			execute: (
				parameter: WebServices.PartnerAdmin.User.Group.GetGroupList.Request
			): Promise<WebServices.PartnerAdmin.User.Group.GetGroupList.Response> =>
				this.get(this.group.list.name, this.GroupAPIPath(), parameter)
		},
		groupDetail: {
			name: "partnerUser:group::groupDetail",
			execute: ({
				groupId,
				...rest
			}: WebServices.PartnerAdmin.User.Group.GroupDetail.Request): Promise<WebServices.PartnerAdmin.User.Group.GroupDetail.Response> =>
				this.get(this.group.groupDetail.name, this.GroupAPIPath(`${groupId}`), rest)
		},
		inquiryDelete: {
			name: "partnerUser:group::inquiryDelete",
			execute: (
				parameter: WebServices.PartnerAdmin.User.Group.GetDeleteUserInfo.Request
			): Promise<WebServices.PartnerAdmin.User.Group.GetDeleteUserInfo.Response> =>
				this.get(this.group.inquiryDelete.name, this.GroupAPIPath("users/delete"), parameter)
		},
		emailCheck: {
			name: "partnerUser:group::emailCheck",
			execute: ({
				requestVO
			}: WebServices.PartnerAdmin.User.Group.AdminEmailCheck.Request): Promise<WebServices.PartnerAdmin.User.Group.AdminEmailCheck.Response> =>
				this.get(this.group.groupDetail.name, this.GroupAPIPath("email_check"), requestVO)
		},
		joinedList: {
			name: "partnerUser:group::joinedList",
			execute: (
				parameter: WebServices.PartnerAdmin.User.Group.GetJoinedGroupList.Request
			): Promise<WebServices.PartnerAdmin.User.Group.GetJoinedGroupList.Response> =>
				this.get(this.group.joinedList.name, this.GroupAPIPath("users"), parameter)
		},
		inquiryWithGroupKey: {
			name: "partnerUser:group::inquiryWithGroupKey",
			execute: (
				parameter: WebServices.PartnerAdmin.User.Group.GroupDetailWithGroupKey.Request
			): Promise<WebServices.PartnerAdmin.User.Group.GroupDetailWithGroupKey.Response> =>
				this.get(this.group.inquiryWithGroupKey.name, this.GroupAPIPath("group_key"), parameter)
		},
		create: {
			name: "partnerUser:group::create",
			execute: (
				parameter: WebServices.PartnerAdmin.User.Group.CreateGroup.Request
			): Promise<WebServices.PartnerAdmin.User.Group.CreateGroup.Response> =>
				this.post(this.update.name, this.GroupAPIPath(""), parameter, {
					headers: { "Content-Type": "multipart/form-data" }
				})
		},
		update: {
			name: "partnerUser:group::update",
			execute: ({
				groupId,
				formData
			}: {
				groupId: string;
				formData: WebServices.PartnerAdmin.User.Group.UpdateGroup.Request;
			}): Promise<WebServices.PartnerAdmin.User.Group.UpdateGroup.Response> =>
				this.post(this.update.name, this.GroupAPIPath(groupId), formData, {
					headers: { "Content-Type": "multipart/form-data" }
				})
		},
		cancelInvite: {
			name: "partnerUser:group::cancelInvite",
			execute: ({
				groupId,
				...rest
			}: WebServices.PartnerAdmin.User.Group.CancelAdminInvite.Request): Promise<WebServices.PartnerAdmin.User.Group.CancelAdminInvite.Response> =>
				this.post(this.group.update.name, this.GroupAPIPath(`${groupId}/invite_cancel`), rest)
		},

		joinGroupWithGroupKey: {
			name: "partnerUser:group::joinGroup",
			execute: (
				parameter: WebServices.PartnerAdmin.User.Group.JoinGroup.Request
			): Promise<WebServices.PartnerAdmin.User.Group.JoinGroup.Response> =>
				this.post(this.group.joinGroupWithGroupKey.name, this.GroupAPIPath("users/group_key"), parameter)
		},
		delete: {
			name: "partnerUser:group::delete",
			execute: (
				parameter: WebServices.PartnerAdmin.User.Group.DeleteGroupUser.Request
			): Promise<WebServices.PartnerAdmin.User.Group.DeleteGroupUser.Response> =>
				this.post(this.group.delete.name, this.GroupAPIPath("users/delete"), parameter)
		},
		changeAdmin: {
			name: "partnerUser:group::changeAdmin",
			execute: (
				parameter: WebServices.PartnerAdmin.User.Group.ChangeAdmin.Request
			): Promise<WebServices.PartnerAdmin.User.Group.ChangeAdmin.Response> =>
				this.put(this.group.changeAdmin.name, this.GroupAPIPath("users/admin"), parameter)
		}
	};
}

class PartnerOpenWorld extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/open_worlds${id ? `/${id}` : ""}`;

	public save = {
		name: "partneradmin:openworld:save",
		execute: (
			formData: WebServices.PartnerAdmin.OpenWorld.Save.Request
		): Promise<WebServices.PartnerAdmin.OpenWorld.Save.Response> =>
			this.post(this.save.name, this.APIPath(), formData, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};

	public update = {
		name: "partneradmin:openworld:update",
		execute: ({
			id,
			formData
		}: {
			id: string;
			formData: WebServices.PartnerAdmin.OpenWorld.Update.Request;
		}): Promise<WebServices.PartnerAdmin.OpenWorld.Update.Response> =>
			this.post(this.update.name, this.APIPath(id), formData, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};

	public remove = {
		name: "partneradmin:openworld:delete",
		execute: ({ id, parameter }: { id: string; parameter: WebServices.PartnerAdmin.OpenWorld.Update.Request }) =>
			this.delete(this.remove.name, this.APIPath(id), parameter)
	};
}

class PartnerAdvertisement extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/advertisements${id ? `/${id}` : ""}`;

	public save = {
		name: "partneradmin:ad:save",
		execute: (
			formData: WebServices.PartnerAdmin.Government.Advertisements.Save.Request
		): Promise<WebServices.PartnerAdmin.Government.Advertisements.Save.Response> =>
			this.post(this.save.name, this.APIPath(), formData, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};

	public update = {
		name: "partneradmin:ad:update",
		execute: ({
			id,
			formData
		}: {
			id: string;
			formData: WebServices.PartnerAdmin.Government.Advertisements.Update.Request;
		}) =>
			this.post(this.update.name, this.APIPath(id), formData, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};

	public list = {
		name: "partneradmin:ad:list",
		execute: ({
			id
		}: WebServices.PartnerAdmin.Government.Advertisements.List.Request): Promise<WebServices.PartnerAdmin.Government.Advertisements.List.Response> =>
			this.get(this.list.name, this.APIPath(id))
	};

	public detail = {
		name: "partneradmin:ad:detail",
		execute: ({
			id
		}: WebServices.PartnerAdmin.Government.Advertisements.Detail.Request): Promise<WebServices.PartnerAdmin.Government.Advertisements.Detail.Response> =>
			this.get(this.detail.name, this.APIPath(id))
	};
}

class PartnerDailyGift extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/daily_gift${id ? `/${id}` : ""}`;

	public save = {
		name: "partneradmin:dailyGift:save",
		execute: (
			parameter: WebServices.PartnerAdmin.Government.DailyGift.Save.Request
		): Promise<WebServices.PartnerAdmin.Government.DailyGift.Save.Response> =>
			this.post(this.save.name, this.APIPath(), parameter)
	};
}

class PartnerNotice extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/notice${id ? `/${id}` : ""}`;

	public remove = {
		name: "partnerAdmin:notice:delete",
		execute: ({ id }: { id: string }): Promise<WebServices.PartnerAdmin.Notice.Delete.Response> =>
			this.delete(this.remove.name, this.APIPath(id))
	};

	public list = {
		name: "partnerAdmin:notice:list",
		execute: (
			parameter: WebServices.PartnerAdmin.Notice.List.Request = {}
		): Promise<WebServices.PartnerAdmin.Notice.List.Response> => this.get(this.list.name, this.APIPath(), parameter)
	};

	public detail = {
		name: "partnerAdmin:notice:detail",
		execute: ({
			id
		}: WebServices.PartnerAdmin.Notice.Detail.Request): Promise<WebServices.PartnerAdmin.Notice.Detail.Response> =>
			this.get(this.detail.name, this.APIPath(id))
	};

	public save = {
		name: "partnerAdmin:notice:save",
		execute: (
			parameter: WebServices.PartnerAdmin.Notice.Save.Request
		): Promise<WebServices.PartnerAdmin.Notice.Save.Response> =>
			this.post(this.save.name, this.APIPath(), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};

	public update = {
		name: "partnerAdmin:notice:update",
		execute: ({
			id,
			parameter
		}: {
			id: string;
			parameter: WebServices.PartnerAdmin.Notice.Update.Request;
		}): Promise<WebServices.PartnerAdmin.Notice.Update.Response> =>
			this.post(this.save.name, this.APIPath(id), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};
}

class PartnerMobileVersion extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (id?: string) => `/api/partner_admin/mobile_versions${id ? `/${id}` : ""}`;

	public remove = {
		name: "partnerAdmin:mobileversion:delete",
		execute: ({ id }: { id: string }): Promise<WebServices.PartnerAdmin.MobileVersion.Remove.Response> =>
			this.delete(this.remove.name, this.APIPath(id))
	};

	public list = {
		name: "partnerAdmin:mobileversion:list",
		execute: (
			parameter: WebServices.PartnerAdmin.MobileVersion.List.Request = {}
		): Promise<WebServices.PartnerAdmin.MobileVersion.List.Response> =>
			this.get(this.list.name, this.APIPath(), parameter)
	};

	public detail = {
		name: "partnerAdmin:mobileversion:detail",
		execute: ({
			id
		}: WebServices.PartnerAdmin.MobileVersion.Detail.Request): Promise<WebServices.PartnerAdmin.MobileVersion.Detail.Response> =>
			this.get(this.detail.name, this.APIPath(id))
	};

	public add = {
		name: "partnerAdmin:mobileversion:add",
		execute: (
			parameter: WebServices.PartnerAdmin.MobileVersion.Add.Request
		): Promise<WebServices.PartnerAdmin.MobileVersion.Add.Response> =>
			this.post(this.add.name, this.APIPath(), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};

	public update = {
		name: "partnerAdmin:mobileversion:update",
		execute: ({
			id,
			parameter
		}: {
			id: string;
			parameter: WebServices.PartnerAdmin.MobileVersion.Update.Request;
		}): Promise<WebServices.PartnerAdmin.MobileVersion.Update.Response> =>
			this.post(this.add.name, this.APIPath(id), parameter, {
				headers: { "Content-Type": "multipart/form-data" }
			})
	};
}
class ExtensionNumber extends BaseServiceV2 {
	constructor() {
		super();
	}

	private APIPath = (suffix: string = "") => `api/partner_admin/extension_numbers${suffix}`;

	public get_detail = {
		name: "partnerAdmin:extensionNumber:detail",
		execute: ({ groupId }: WebServices.PartnerAdmin.ExtensionNumber.Detail.Request) =>
			this.get(this.get_detail.name, this.APIPath(`/${groupId}`))
	};

	public get_user_detail = {
		name: "partnerAdmin:extensionNumber:userDetail",
		execute: ({ groupId, userId }: WebServices.PartnerAdmin.ExtensionNumber.UserDetail.Request) =>
			this.get(this.get_user_detail.name, this.APIPath(`/${groupId}/users/${userId}`))
	};

	public get_users = {
		name: "partnerAdmin:extensionNumber:users",
		execute: ({ groupId, ...rest }: WebServices.PartnerAdmin.ExtensionNumber.Users.Request) =>
			this.get(this.get_users.name, this.APIPath(`/${groupId}/users`), rest)
	};

	public get_auto_assign_status = {
		name: "partnerAdmin:extensionNumber:autoAssignStatus",
		execute: ({ groupId }: WebServices.PartnerAdmin.ExtensionNumber.AutoAssignStatus.Request) =>
			this.get(this.get_auto_assign_status.name, this.APIPath(`/${groupId}/auto_assign`))
	};

	public get_unassigned_group_list = {
		name: "partnerAdmin:extensionNumber:unassignedGroupList",
		execute: ({ partnerId }: WebServices.PartnerAdmin.ExtensionNumber.UnAssignedGroupList.Request) =>
			this.get(this.get_group_list.name, this.APIPath("/unassigned_groups"), { partnerId })
	};

	public get_group_list = {
		name: "partnerAdmin:extensionNumber:groupList",
		execute: ({ partnerId, ...rest }: WebServices.PartnerAdmin.ExtensionNumber.GroupList.Request) =>
			this.get(this.get_group_list.name, this.APIPath("/groups"), { partnerId, ...rest })
	};

	public get_assignable = {
		name: "partnerAdmin:extensionNumber:assignable",
		execute: ({ requestVO }: WebServices.PartnerAdmin.ExtensionNumber.Assignable.Request) =>
			this.get(this.get_assignable.name, this.APIPath("/check"), requestVO)
	};

	public save_extension_number = {
		name: "partnerAdmin:extensionNumber:save",
		execute: (rest: WebServices.PartnerAdmin.ExtensionNumber.Save.Request) =>
			this.post(this.save_extension_number.name, this.APIPath(), rest)
	};

	public update_user_extension_number = {
		name: "partnerAdmin:extensionNumber:updateUser",
		execute: ({ groupId, userId, ...rest }: WebServices.PartnerAdmin.ExtensionNumber.UpdateUserNumber.Request) =>
			this.post(this.update_user_extension_number.name, this.APIPath(`/${groupId}/users/${userId}`), { ...rest })
	};

	public update_extension_number = {
		name: "partnerAdmin:extensionNumber:update",
		execute: ({ groupId, ...rest }: WebServices.PartnerAdmin.ExtensionNumber.Update.Request) =>
			this.put(this.update_extension_number.name, this.APIPath(`/${groupId}`), { ...rest })
	};
}

export default class PartnerService {
	constructor(
		events: API.Events,
		public readonly partnerAdmin = {
			defaultProfileImage: new PartnerAdminDefaultProfileImage(),
			defaultUserStatus: new PartnerAdminDefaultUserStats(),
			emoticon: new PartnerAdminEmoticon(),
			spaceMap: new PartnerAdminSpaceMap(),
			spaceTheme: new PartnerAdminSpaceTheme(),
			openWorld: new PartnerOpenWorld(),
			advertisement: new PartnerAdvertisement(),
			dailyGift: new PartnerDailyGift(),
			notice: new PartnerNotice(),
			user: new PartnerNormalUser(),
			mobileVersion: new PartnerMobileVersion(),
			extensionNumber: new ExtensionNumber()
		},
		public readonly partner = new Partner(),
		public readonly partnerUser = new PartnerUser()
	) {
		partnerAdmin.defaultProfileImage.set_event(events);
		partnerAdmin.defaultUserStatus.set_event(events);
		partnerAdmin.emoticon.set_event(events);
		partnerAdmin.spaceMap.set_event(events);
		partnerAdmin.spaceTheme.set_event(events);
		partnerAdmin.openWorld.set_event(events);
		partnerAdmin.advertisement.set_event(events);
		partnerAdmin.dailyGift.set_event(events);
		partnerAdmin.extensionNumber.set_event(events);
		partner.set_event(events);
		partnerUser.set_event(events);
	}
}
